// src/services/PackagePurchaseHandler.js
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  query,
  where,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";
import ReferralCommissionService from "./ReferralCommissionService";
import {
  checkMilestoneEmailPending,
  notifyMilestoneAchievement,
  markMilestoneEmailSent,
} from "./MilestoneService";

class PackagePurchaseHandler {
  static async processPurchase({
    buyerId,
    buyerName,
    buyerEmail,
    package: packageData,
    userProfile,
    includesRegistrationFee = false,
  }) {
    try {
      console.log("Processing package purchase...", {
        buyerId,
        packageId: packageData.id,
        packageName: packageData.name,
        includesRegistrationFee,
      });

      // Generate order ID
      const orderId = `PKG-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`;

      // Calculate amounts
      const packagePrice = packageData.price;
      const registrationFee = includesRegistrationFee ? 499 : 0;
      const totalAmount = packagePrice + registrationFee;

      // Create order document
      const orderData = {
        orderId,
        type: "package",
        buyerId,
        buyerName,
        buyerEmail,
        packageId: packageData.id,
        packageName: packageData.name,
        packagePrice,
        registrationFee,
        totalAmount,
        paymentStatus: "pending",
        adminConfirmed: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        includesRegistrationFee,
        // Order details
        orderDetails: {
          packageCategory: packageData.category,
          packageDescription: packageData.description,
          packageFeatures: packageData.features || [],
        },
        // User details
        userDetails: {
          referralIdUsed: userProfile?.referralIdUsed || null,
          myReferralId: userProfile?.myReferralId || null,
          isFirstPackagePurchase: !userProfile?.hasPurchasedPackage,
        },
      };

      // Add order to database
      const orderRef = await addDoc(collection(db, "orders"), orderData);
      console.log("Order created:", orderRef.id);

      // If this includes registration fee, update user's registration status
      if (includesRegistrationFee) {
        await this.updateUserRegistrationStatus(buyerId, true);
      }

      // Mark that user has purchased a package (for future referral eligibility)
      await this.markUserAsPackagePurchaser(buyerId);

      // Check for milestone achievements for referrers
      await this.checkReferrerMilestones(buyerId);

      // Process referral commissions (only if user was referred and referrer has purchased packages)
      if (userProfile?.referralIdUsed) {
        await this.processReferralCommissions({
          orderId,
          buyerId,
          buyerName,
          packagePrice,
          referralIdUsed: userProfile.referralIdUsed,
        });
      }

      return {
        success: true,
        orderId,
        orderDocId: orderRef.id,
        totalAmount,
        message: "Package purchase processed successfully",
      };
    } catch (error) {
      console.error("Error processing package purchase:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  static async updateUserRegistrationStatus(userId, paid = true) {
    try {
      // Find user document
      const userQuery = query(
        collection(db, "users"),
        where("uid", "==", userId)
      );
      const userSnapshot = await getDocs(userQuery);

      if (!userSnapshot.empty) {
        const userDocRef = doc(db, "users", userSnapshot.docs[0].id);

        await updateDoc(userDocRef, {
          "registrationFee.paid": paid,
          "registrationFee.paidAt": paid ? new Date().toISOString() : null,
          updatedAt: new Date().toISOString(),
        });

        console.log("User registration status updated");
      }
    } catch (error) {
      console.error("Error updating user registration status:", error);
    }
  }

  static async checkReferrerMilestones(newUltraCustomerId) {
    try {
      // Find who referred this new Ultra customer
      const newUltraQuery = query(
        collection(db, "users"),
        where("uid", "==", newUltraCustomerId)
      );
      const newUltraSnapshot = await getDocs(newUltraQuery);
      
      if (newUltraSnapshot.empty) return;
      
      const newUltraData = newUltraSnapshot.docs[0].data();
      const referralIdUsed = newUltraData.referralIdUsed;
      
      if (!referralIdUsed) return;
      
      // Find the referrer
      const referrerQuery = query(
        collection(db, "users"),
        where("myReferralId", "==", referralIdUsed)
      );
      const referrerSnapshot = await getDocs(referrerQuery);
      
      if (referrerSnapshot.empty) return;
      
      const referrerData = referrerSnapshot.docs[0].data();
      
      // Check milestones for the referrer
      const emailPending = await checkMilestoneEmailPending(
        referrerData.uid,
        referrerData
      );
      if (emailPending) {
        console.log(
          `🎉 Milestone email for referrer ${referrerData.uid}: ${emailPending.milestone} Ultra customers`
        );
        await notifyMilestoneAchievement(
          referrerData,
          emailPending.milestone
        );
        await markMilestoneEmailSent(
          referrerData.uid,
          emailPending.milestone
        );
      }
    } catch (error) {
      console.error("Error checking referrer milestones:", error);
    }
  }

  static async markUserAsPackagePurchaser(userId) {
    try {
      // Find user document
      const userQuery = query(
        collection(db, "users"),
        where("uid", "==", userId)
      );
      const userSnapshot = await getDocs(userQuery);

      if (!userSnapshot.empty) {
        const userDocRef = doc(db, "users", userSnapshot.docs[0].id);

        await updateDoc(userDocRef, {
          hasPurchasedPackage: true,
          firstPackagePurchaseAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });

        console.log("User marked as package purchaser");
      }
    } catch (error) {
      console.error("Error marking user as package purchaser:", error);
    }
  }

  static async processReferralCommissions({
    orderId,
    buyerId,
    buyerName,
    packagePrice,
    referralIdUsed,
  }) {
    try {
      console.log("Processing referral commissions for package purchase...");

      // Find the referrer using the referral ID
      const referrerQuery = query(
        collection(db, "users"),
        where("myReferralId", "==", referralIdUsed)
      );
      const referrerSnapshot = await getDocs(referrerQuery);

      if (referrerSnapshot.empty) {
        console.log("Referrer not found for referral ID:", referralIdUsed);
        return;
      }

      const referrerData = referrerSnapshot.docs[0].data();
      const referrerId = referrerData.uid;

      // Check if referrer has purchased a package (new requirement)
      if (!referrerData.hasPurchasedPackage) {
        console.log(
          "Referrer has not purchased a package, no points given"
        );
        return;
      }

      // Calculate commission (example: 10% of package price)
      const commissionRate = 0.1; // 10%
      const commissionAmount = packagePrice * commissionRate;

      // Create commission record
      const commissionData = {
        commissionId: `COMM-${Date.now()}-${Math.random()
          .toString(36)
          .substr(2, 9)}`,
        type: "package_referral",
        orderId,
        referrerId,
        referrerName: referrerData.name,
        referrerEmail: referrerData.email,
        buyerId,
        buyerName,
        packagePrice,
        commissionAmount,
        commissionRate,
        status: "pending", // Will be confirmed when admin confirms the order
        createdAt: new Date().toISOString(),
        notes: `Points for package referral: ${referralIdUsed}`,
      };

      // Add commission record
      await addDoc(collection(db, "commissions"), commissionData);
      console.log("Referral points record created");

      // Update referrer's total points (pending)
      await this.updateReferrerCommissionStats(referrerId, commissionAmount);
    } catch (error) {
      console.error("Error processing referral commissions:", error);
    }
  }

  static async updateReferrerCommissionStats(referrerId, commissionAmount) {
    try {
      // Find referrer document
      const referrerQuery = query(
        collection(db, "users"),
        where("uid", "==", referrerId)
      );
      const referrerSnapshot = await getDocs(referrerQuery);

      if (!referrerSnapshot.empty) {
        const referrerDocRef = doc(db, "users", referrerSnapshot.docs[0].id);
        const currentData = referrerSnapshot.docs[0].data();

        const currentPendingCommissions = currentData.pendingCommissions || 0;
        const currentTotalCommissions = currentData.totalCommissions || 0;

        await updateDoc(referrerDocRef, {
          pendingCommissions: currentPendingCommissions + commissionAmount,
          totalCommissions: currentTotalCommissions + commissionAmount,
          updatedAt: new Date().toISOString(),
        });

        console.log("Referrer points stats updated");
      }
    } catch (error) {
      console.error("Error updating referrer points stats:", error);
    }
  }

  // Method to confirm order and commissions (called by admin)
  static async confirmOrder(orderId) {
    try {
      console.log("Confirming order:", orderId);

      // Find order
      const orderQuery = query(
        collection(db, "orders"),
        where("orderId", "==", orderId)
      );
      const orderSnapshot = await getDocs(orderQuery);

      if (orderSnapshot.empty) {
        throw new Error("Order not found");
      }

      const orderDocRef = doc(db, "orders", orderSnapshot.docs[0].id);
      const orderData = orderSnapshot.docs[0].data();

      // Update order status
      await updateDoc(orderDocRef, {
        paymentStatus: "confirmed",
        adminConfirmed: true,
        confirmedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });

      // If order included registration fee, confirm user registration
      if (orderData.includesRegistrationFee) {
        await this.confirmUserRegistration(orderData.buyerId);
      }

      // Confirm any related commissions
      await this.confirmCommissions(orderId);

      console.log("Order confirmed successfully");
      return { success: true };
    } catch (error) {
      console.error("Error confirming order:", error);
      return { success: false, error: error.message };
    }
  }

  static async confirmUserRegistration(userId) {
    try {
      // Find user document
      const userQuery = query(
        collection(db, "users"),
        where("uid", "==", userId)
      );
      const userSnapshot = await getDocs(userQuery);

      if (!userSnapshot.empty) {
        const userDocRef = doc(db, "users", userSnapshot.docs[0].id);

        await updateDoc(userDocRef, {
          "registrationFee.confirmedByAdmin": true,
          "registrationFee.confirmedAt": new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });

        console.log("User registration confirmed by admin");
      }
    } catch (error) {
      console.error("Error confirming user registration:", error);
    }
  }

  static async confirmCommissions(orderId) {
    try {
      // Find commissions for this order
      const commissionsQuery = query(
        collection(db, "commissions"),
        where("orderId", "==", orderId)
      );
      const commissionsSnapshot = await getDocs(commissionsQuery);

      // Update each commission to confirmed status
      for (const commissionDoc of commissionsSnapshot.docs) {
        const commissionRef = doc(db, "commissions", commissionDoc.id);
        await updateDoc(commissionRef, {
          status: "confirmed",
          confirmedAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
      }

      console.log(`${commissionsSnapshot.docs.length} commissions confirmed`);
    } catch (error) {
      console.error("Error confirming commissions:", error);
    }
  }
}

export default PackagePurchaseHandler;
